import { Reveal } from "@/lib/motion";
import { Chapter } from "./Chapter";
import { EDUCATION, CERTS } from "@/data/content";
import { GraduationCap, ShieldCheck, ExternalLink } from "lucide-react";

const openPdfResponsive = (url) => (e) => {
  e.preventDefault();
  const frag = window.innerWidth < 768 ? "#view=FitH" : "#zoom=105";
  window.open(`${url}${frag}`, "_blank", "noopener,noreferrer");
};

export const Education = () => (
  <section id="education" className="relative z-10 py-24 md:py-32 lg:py-40">
    <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20">
      <Chapter num="04" title="Education & credentials" kicker="Degree · Certifications" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <Reveal className="lg:col-span-7">
          <div className="h-full border-t-2 border-[#16324f] pt-8">
            <GraduationCap size={30} strokeWidth={1.4} className="text-[#b5423a] mb-6" />
            <h3 className="font-serif-display text-2xl md:text-3xl text-[#16324f] leading-tight">
              {EDUCATION.school}
            </h3>
            <p className="mt-3 text-[16px] italic text-[#48606f]">{EDUCATION.degree}</p>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {EDUCATION.tags.map((t) => (
                <span
                  key={t}
                  className="font-mono-plex text-[12.5px] px-3.5 py-1.5 rounded-full border border-[#d2d5ce] text-[#48606f]"
                >
                  {t}
                </span>
              ))}
            </div>
            {EDUCATION.pdf && (
              <a
                data-testid="education-degree-link"
                href={`${EDUCATION.pdf}#zoom=105`}
                onClick={openPdfResponsive(EDUCATION.pdf)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-7 inline-flex items-center gap-1.5 font-mono-plex text-[11px] uppercase tracking-[0.12em] text-[#b5423a] hover:text-[#16324f] transition-colors duration-300"
              >
                <ExternalLink size={12} /> View degree certificate
              </a>
            )}
          </div>
        </Reveal>

        <div className="lg:col-span-5 space-y-5">
          {CERTS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.1}>
              <div className="group bg-[#f5f6f2] border border-[#d2d5ce] hover:border-[#16324f] transition-colors duration-500 p-6 md:p-7">
                <div className="flex items-center gap-2 mb-4">
                  <ShieldCheck size={16} className="text-[#2e7d6b]" />
                  <span className="eyebrow !text-[#2e7d6b]">Certificate</span>
                </div>
                <h3 className="font-serif-display text-xl text-[#16324f] leading-snug">{c.title}</h3>
                <p className="mt-2.5 text-[14.5px] text-[#48606f] leading-relaxed">{c.desc}</p>
                {(c.issuer || c.pdf) && (
                  <div className="mt-5 pt-4 border-t border-[#d2d5ce] flex items-center justify-between gap-3 flex-wrap">
                    {c.issuer && (
                      <span className="font-mono-plex text-[11px] uppercase tracking-[0.13em] text-[#48606f]">
                        {c.issuer}
                      </span>
                    )}
                    {c.pdf && (
                      <a
                        data-testid={`cert-link-${i}`}
                        href={c.pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 font-mono-plex text-[11px] uppercase tracking-[0.12em] text-[#b5423a] hover:text-[#16324f] transition-colors duration-300"
                      >
                        <ExternalLink size={12} /> View certificate
                      </a>
                    )}
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);
